import {
  ArdoqId,
  AuditLogEntityType,
  APIReportFilters,
  DashboardFilterValues,
  ViewIds,
} from '@ardoq/api-types';
import type { AdHocSearchReport } from '@ardoq/report-builder';
import type { I18nLocale } from '@ardoq/i18n';
import { actionCreator, type ActionWithPayload } from '@ardoq/rxbeach';
import { OrgTabs } from 'admin/manageOrganization/types';
import { UserSettingsTabs } from 'admin/user/types';
import { AppModules } from 'appContainer/types';
import {
  MetamodelEntityId,
  TabId,
} from 'organizationMetamodel/workspaces/metamodel/types';
import { ScenarioModuleRoute, WorkspaceModuleRoute } from './appRouterTypes';
import { RouterLocation } from './StreamRouter';
import type { PaneLocation } from 'streams/views/mainContent/types';
import {
  Trackable,
  trackingData,
  TrackingEvent,
} from '../tracking/trackPayload';
import { navigate } from './declarative/streamRouterAdapter/actions';
import type { AppLocation } from './declarative/location$';

type NavigateAction = ActionWithPayload<Trackable<AppLocation>>;

export type NavigateToReportModulePayload = {
  reportId?: ArdoqId;
  filters?: APIReportFilters;
  dashboardId?: ArdoqId;
  dashboardFilterValues?: DashboardFilterValues;
  stateId?: ArdoqId;
  folderId?: ArdoqId;
  presentation?: { presentationId: ArdoqId; selectedSlide: ArdoqId };
};

export const navigateToReportReader = actionCreator<
  Trackable<NavigateToReportModulePayload>
>('[navigationActions] NAVIGATE_TO_REPORT_READER');

export type ReportCopySeed = {
  copyOfReportId: ArdoqId;
  name: string;
};

export type NavigateToReportBuilderPayload =
  | NavigateToReportModulePayload
  | AdHocSearchReport
  | ReportCopySeed;

export const navigateToReportBuilder =
  actionCreator<NavigateToReportBuilderPayload>(
    '[navigationActions] NAVIGATE_TO_REPORT_BUILDER'
  );

export const navigateToReportOverview = actionCreator<Trackable<object> | void>(
  '[navigationActions] NAVIGATE_TO_REPORT_OVERVIEW'
);

export const setPreviousAppModule = actionCreator<AppModules>(
  '[navigationActions] SET_PREVIOUS_APP_MODULE'
);

export const selectOrganizationMetamodelWorkspace =
  actionCreator<ArdoqId | null>(
    '[navigationActions] NAVIGATE_TO_ORGANIZATION_METAMODEL_WORKSPACE'
  );

export const selectOrganizationMetamodelTab = actionCreator<TabId>(
  '[navigationActions] NAVIGATE_TO_ORGANIZATION_METAMODEL_TAB'
);

export const selectOrganizationMetamodelEntity =
  actionCreator<MetamodelEntityId>(
    '[navigationActions] SELECT_ORGANIZATION_METAMODEL_ENTITY'
  );

export const navigateToBroadcastOverview = (trackingEvent?: TrackingEvent) =>
  navigate({
    route: '/broadcasts',
    params: {},
    [trackingData]: trackingEvent,
  });

export const navigateToPresentationAdmin = (trackingEvent?: TrackingEvent) =>
  navigate({
    route: '/presentation-admin',
    params: {},
    [trackingData]: trackingEvent,
  });

export const navigateToUserSettings = (
  tab: UserSettingsTabs = UserSettingsTabs.PROFILE
): NavigateAction =>
  navigate({ route: `/account-settings/${tab}`, params: {} });

export const navigateToViewpoints = (): NavigateAction =>
  navigate({ route: '/viewpoints', params: {} });

export const navigateToNewViewpointForm = (): NavigateAction =>
  navigate({ route: '/viewpoints/legacy/new', params: {} });

export const navigateToViewpointForm = (viewpointId: ArdoqId): NavigateAction =>
  navigate({
    route: '/viewpoints/legacy/:entityId',
    params: { entityId: viewpointId },
  });

export const navigateToDefaultViewpoints = (): NavigateAction =>
  navigate({ route: '/viewpoints/default', params: {} });

export const navigateToViewpoint = (viewpointId: ArdoqId): NavigateAction =>
  navigate({
    route: '/viewpoints/:entityId',
    params: { entityId: viewpointId },
  });

export const initiateNavigationToNewBroadcastForm =
  actionCreator<ArdoqId | null>(
    '[navigationActions] INITIATE_NAVIGATION_TO_NEW_BROADCAST_FORM'
  );

export const navigateToNewBroadcastForm = actionCreator<{
  folderId: ArdoqId | null;
  i18nLanguage: string;
  i18nLocale: I18nLocale;
}>('[navigationActions] NAVIGATE_TO_NEW_BROADCAST_FORM');

export const initiateNavigationToEditBroadcastForm = actionCreator<ArdoqId>(
  '[navigationActions] INITIATE_NAVIGATION_TO_EDIT_BROADCAST_FORM'
);

export const navigateToEditBroadcastForm = actionCreator<ArdoqId>(
  '[navigationActions] NAVIGATE_TO_EDIT_BROADCAST_FORM'
);

export const navigateToManageOrganization = actionCreator<
  Trackable<{
    manageOrgTab: OrgTabs;
  }>
>('[navigationActions] NAVIGATE_TO_MANAGE_ORGANIZATION');

export const loadWorkspaceModuleRoute = actionCreator<WorkspaceModuleRoute>(
  '[navigationActions] LOAD_WORKSPACE_MODULE_ROUTE'
);

export const loadScenarioModuleRoute = actionCreator<ScenarioModuleRoute>(
  '[navigationActions] LOAD_SCENARIO_MODULE_ROUTE'
);

export const syncRouteActiveViews = actionCreator<{
  mainViewId: ViewIds;
  secondaryViewId: ViewIds | null;
  isViewpointMode: boolean;
}>('[navigationActions] SYNC_ROUTE_ACTIVE_VIEWS');

export type NavigateToAuditLogPayload = Trackable<
  | {
      entityType: AuditLogEntityType;
      entities: Array<{ id: ArdoqId; name?: string }>;
    }
  | object
> | null;

export const navigateToAuditLog = actionCreator<NavigateToAuditLogPayload>(
  '[navigationActions] NAVIGATE_TO_AUDIT_LOG'
);

export const navigateToAnalyticsOverview = actionCreator(
  '[navigationActions] NAVIGATE_TO_ANALYTICS_OVERVIEW'
);

/**
 * @deprecated Only use for routes handled by the legacy `StreamRouter`. For
 * declarative routes, use `navigate` from
 * `router/declarative/streamRouterAdapter/actions`.
 */
export const navigateOld = actionCreator<Trackable<RouterLocation>>(
  '[navigationActions] NAVIGATE_OLD'
);

export type NavigatorComponentClickedPayload = {
  componentId: ArdoqId;
};

export const navigatorComponentClicked =
  actionCreator<NavigatorComponentClickedPayload>(
    '[navigationActions] NAVIGATOR_COMPONENT_CLICKED'
  );

export type ViewTabClickedPayload = {
  paneLocation: PaneLocation;
  viewId: ViewIds;
};

export const viewTabClicked = actionCreator<ViewTabClickedPayload>(
  '[navigationActions] VIEW_TAB_CLICKED'
);
